// Subscribe Sign form widget — state-aware (Phase 6B).
//
// Variant lookup driven by the subscription's dossier state. Replaces
// the Phase 2.4b mock setTimeout flow with real Privy EIP-712 signing.
//
//   currentPhase    Variant
//   ──────────────  ──────────
//   04-allocated    idle  (agreement scroll + 3 confirmations + Sign)
//   05-signed       done  (signature hash + Continue→Fund CTA)
//   ≥ 06-*          historic (read-only "previously signed" footer)
//
// On Sign click, the widget builds the EIP-712 typed data via the shared
// @rmint/core/signing/subscription-agreement module, asks Privy to sign,
// then POSTs subscription.signed. The projector's verifier (apps/projector
// /src/routes/sign-verify.ts) recovers the signer + rejects if it doesn't
// match the investor's wallet on file. After 200, refetchKey bumps and
// state.current re-reads to flip the variant.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Address, Button } from '@rmint/ui';
import { useAuth } from '@rmint/shell';
import { useSubscriptionState } from '../../lib/use-subscription-state';
import { useOffering, type Offering } from '../../lib/offering';
import { useSubscriptionSigner } from '../../lib/sign-subscription';
import { env } from '@rmint/shell';
import type { WidgetProps } from './registry';

const FALLBACK_OFFERING: Offering = {
  rmintId: 'sakura-ramen-test-d1',
  restaurantName: 'Sakura Ramen NC',
  ticker: 'RMOT-SAKURA-D1',
  tokenContractAddress: '0xB03e42d3B2F86343dEcC3631bBd792F7f988eCAd',
  saleContractAddress: '0xf8FF966494E91283Ca7b744c60F12811C6bD92dC',
  pricePerToken: 1.0,
  minAmountUsdc: 1000,
  maxAmountUsdc: 50000,
  lockupMonths: 36,
  lockupEndIso: '2029-05-09T00:00:00Z',
  signByDeadlineIso: '2026-06-09T00:00:00Z',
  estimatedFirstDistributionIso: '2026-08-15T00:00:00Z',
  networkFeeUsdcEst: 2.5,
};

const FALLBACK_AGREEMENT_TEXT = `This Subscription Agreement is entered into between Investor and Issuer. Investor irrevocably subscribes for RMOT-SAKURA-D1 tokens. A 36-month lockup applies.`;

type Variant = 'idle' | 'done' | 'historic';

const DONE_PHASES = new Set(['05-signed']);
const HISTORIC_PHASES = new Set([
  '06-purchased',
  '07-holding',
  '08-settled',
  '09-refunded',
  '10-kyc-rejected',
]);

// Exported for unit testing — pure function, no React.
export function selectSignVariant(currentPhase: string | undefined): Variant {
  if (!currentPhase) return 'idle';
  if (DONE_PHASES.has(currentPhase)) return 'done';
  if (HISTORIC_PHASES.has(currentPhase)) return 'historic';
  return 'idle';
}

function chainIdForEnv(envName: string): number {
  if (envName === 'base') return 8453;
  if (envName === 'local') return 31337;
  return 84532;
}

export function SubscribeSignForm(props: WidgetProps) {
  const navigate = useNavigate();
  const { walletAddress } = useAuth();
  const subscriptionId =
    (props.subscriptionId as string | undefined) ??
    (props.subscriptionid as string | undefined) ??
    '';

  const debugStateOverride = props.state as string | undefined;

  const [refetchKey, setRefetchKey] = useState(0);
  const sub = useSubscriptionState(subscriptionId, refetchKey);

  const dossierVariant = selectSignVariant(sub.state?.currentPhase);
  const overrideVariant: Variant | null =
    debugStateOverride === 'done'
      ? 'done'
      : debugStateOverride === 'historic'
        ? 'historic'
        : null;
  const variant: Variant = overrideVariant ?? dossierVariant;

  const dossierRmintId = (sub.state?.raw.data as { rmintId?: string } | undefined)?.rmintId;
  const propRmintId = props.rmintId as string | undefined;
  const rmintId = dossierRmintId ?? propRmintId;
  const off = useOffering(rmintId);
  const offering: Offering = off.offering ?? FALLBACK_OFFERING;
  const agreementText =
    (off.offering as (Offering & { agreementText?: string }) | null)?.agreementText ??
    FALLBACK_AGREEMENT_TEXT;

  const signer = useSubscriptionSigner();
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [confirmedCount, setConfirmedCount] = useState(0);

  const subData = sub.state?.raw.data as { investorId?: string; capUsd?: number } | undefined;

  async function handleSign() {
    if (!walletAddress || !subscriptionId || !rmintId || !subData) {
      setSignError('Missing context — reload and try again.');
      return;
    }
    const capUsd = subData.capUsd;
    if (typeof capUsd !== 'number') {
      setSignError('Allocation cap not set — officer must issue allocation.issued first.');
      return;
    }
    setSigning(true);
    setSignError(null);
    try {
      const signed = await signer.signSubscriptionAgreement({
        subscriptionId,
        investor: walletAddress as `0x${string}`,
        restaurantId: rmintId,
        amountUsdc: capUsd,
        tokens: Math.floor(capUsd / offering.pricePerToken),
        pricePerToken: offering.pricePerToken,
        lockupMonths: offering.lockupMonths,
        nonce: sub.state?.raw.version ?? 0,
        chainId: chainIdForEnv(env.chainEnv),
        saleContractAddress: offering.saleContractAddress,
        agreementText,
      });
      const res = await fetch('/events', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          entityType: 'subscription',
          entityId: subscriptionId,
          eventId: `evt_signed_${crypto.randomUUID()}`,
          eventType: 'subscription.signed',
          occurredAt: signed.signedAt,
          body: {
            subject: { subscriptionId, investorId: subData.investorId ?? 'inv_unknown', rmintId },
            data: {
              agreementHash: signed.agreementHash,
              signature: signed.signature,
              signedAt: signed.signedAt,
            },
          },
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { kind?: string } };
        throw new Error(`sign verification failed (${res.status}): ${body.error?.kind ?? 'unknown'}`);
      }
      setRefetchKey((k) => k + 1);
      setSigning(false);
    } catch (e) {
      setSignError((e as Error).message);
      setSigning(false);
    }
  }

  // ─── Render branches ──────────────────────────────────────────────

  if (variant === 'done') {
    const sigData = sub.state?.raw.data as { signature?: string } | undefined;
    const sig = sigData?.signature ?? '0x' + '0'.repeat(130);
    return (
      <div className="rounded-card border border-border bg-card p-6 space-y-4">
        <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-[13px]">
          <dt className="text-foreground-mute">Signature</dt>
          <dd className="font-mono text-right m-0">
            <output>{`${sig.slice(0, 10)}…${sig.slice(-8)}`}</output>
          </dd>
        </dl>
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(`/subscribe/${encodeURIComponent(subscriptionId)}/fund`)}
          >
            Continue to Fund →
          </Button>
        </div>
      </div>
    );
  }

  if (variant === 'historic') {
    return (
      <div className="space-y-4">
        <div className="rounded-card border border-border bg-card p-4 space-y-1 text-[13px]">
          <p className="font-semibold text-ink">Subscription agreement signed</p>
          <p className="text-[11px] text-foreground-mute">
            This subscription is past the signing step.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(`/subscribe/${encodeURIComponent(subscriptionId)}/fund`)}
          >
            Continue to Fund →
          </Button>
        </div>
      </div>
    );
  }

  // idle
  const allConfirmed = confirmedCount >= 3;
  const ctaDisabled =
    !walletAddress ||
    !allConfirmed ||
    signing ||
    sub.loading ||
    off.loading ||
    !off.offering;

  return (
    <form
      className="rounded-card border border-border bg-card overflow-hidden"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ctaDisabled) handleSign();
      }}
    >
      <header className="flex items-center justify-between border-b border-border-soft px-6 py-4">
        <h2 className="text-lg font-bold text-ink">Subscription agreement</h2>
        <span className="font-mono text-[11px] text-foreground-mute">step 3 / 5</span>
      </header>
      <div className="px-6 py-5">
        {walletAddress ? (
          <Address
            value={walletAddress as `0x${string}`}
            variant="full"
            status="connected"
            label="Signing as"
          />
        ) : (
          <div className="flex items-center gap-3 rounded-card border border-dashed border-border-strong bg-muted px-4 py-3 text-sm text-foreground-mute">
            <span aria-hidden className="inline-block w-2 h-2 rounded-full shrink-0 bg-foreground-mute" />
            <span>Connect a wallet to sign this subscription.</span>
          </div>
        )}

        <div className="my-4 max-h-56 overflow-auto rounded-card border border-border bg-muted px-5 py-4 font-mono text-xs leading-relaxed text-ink whitespace-pre-wrap">
          {agreementText}
        </div>

        <Confirmations onChange={(count) => setConfirmedCount(count)} />

        {signError ? (
          <div className="mt-3 rounded-card border border-warn-line bg-warn-bg px-4 py-3 text-[13px] text-warn">
            {signError}
          </div>
        ) : null}
      </div>
      <footer className="flex items-center justify-between border-t border-border-soft bg-muted px-6 py-4">
        <Link
          to={`/subscribe/${encodeURIComponent(subscriptionId)}/allocate`}
          className="px-4 py-3 text-sm font-semibold text-ink hover:bg-card rounded-input"
        >
          ← Back to Allocate
        </Link>
        <Button variant="primary" size="lg" type="submit" disabled={ctaDisabled}>
          {signing ? 'Waiting for wallet…' : 'Review & sign in wallet →'}
        </Button>
      </footer>
    </form>
  );
}

function Confirmations({ onChange }: { onChange: (count: number) => void }) {
  const [checked, setChecked] = useState<boolean[]>([false, false, false]);
  const labels = [
    'I have read and agree to the Subscription Agreement and Risk Disclosures.',
    'I confirm I am an accredited investor as defined under 17 CFR §230.501.',
    'I understand my tokens are subject to a 36-month lockup and limited transferability.',
  ];
  function toggle(i: number) {
    const next = checked.slice();
    next[i] = !next[i];
    setChecked(next);
    onChange(next.filter(Boolean).length);
  }
  return (
    <fieldset className="space-y-0 border-0 p-0 m-0">
      <legend className="sr-only">Subscription confirmations</legend>
      {labels.map((label, i) => (
        <label
          key={i}
          className="flex items-start gap-2.5 border-t border-border-soft py-2.5 text-[13px] leading-relaxed text-ink first:border-t-0"
        >
          <input
            type="checkbox"
            checked={checked[i]!}
            onChange={() => toggle(i)}
            className="mt-1 h-4 w-4 shrink-0 accent-primary"
          />
          <span>{label}</span>
        </label>
      ))}
    </fieldset>
  );
}
